// TechStackTemplate — infographic.
// A wall of technology chips, optionally grouped. Each chip has a name and
// optional version. Subtle hairline borders, monospace versions.
// Reference: GitHub "Built with" sections, Linear changelog footer chips.

import { motion } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Eyebrow } from '../stage/Eyebrow';
import { PointableItem, DefaultOrbAnchor } from '../stage/PointableItem';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface TechStackProps {
  content: SceneContent;
}

export function TechStackTemplate({ content }: TechStackProps) {
  const { headline, eyebrow, body, chips = [] } = content;
  // Group chips if any have a `group` property
  const groups = new Map<string, typeof chips>();
  for (const chip of chips) {
    const g = chip.group ?? '';
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(chip);
  }
  const grouped = chips.some((c) => c.group);

  return (
    <Stage>
      {/* Orb anchor — top-right, 48px. Hidden when pointing at a chip. */}
      <div className="absolute top-12 right-12 max-md:top-6 max-md:right-6">
        <DefaultOrbAnchor size={48} />
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.1)}
        className="m-auto w-full max-w-[1280px] flex flex-col items-center text-center gap-12"
      >
        {eyebrow && (
          <motion.div variants={fadeUp}>
            <Eyebrow>{eyebrow}</Eyebrow>
          </motion.div>
        )}
        {headline && (
          <motion.h2
            variants={fadeUp}
            className="text-display-2 font-display max-w-[24ch] text-balance text-fg"
          >
            {headline}
          </motion.h2>
        )}
        {body && (
          <motion.p variants={fadeUp} className="text-subhead text-fg-mute max-w-[52ch]">
            {body}
          </motion.p>
        )}

        {grouped ? (
          <motion.div variants={stagger(0.08)} className="flex flex-col gap-8 w-full max-w-[960px]">
            {(() => {
              // Walk groups in iteration order while keeping a single
              // running index across the whole chip array — that way the
              // agent can address chips by `chip_${i}` regardless of
              // grouping shape (matches the un-grouped path below).
              let runningIndex = 0;
              return Array.from(groups.entries()).map(([groupName, groupChips], gi) => (
                <motion.div
                  key={gi}
                  variants={fadeUp}
                  className="flex flex-col gap-4 items-center"
                >
                  {groupName && (
                    <span className="text-eyebrow text-fg-mute uppercase font-medium">{groupName}</span>
                  )}
                  <div className="flex flex-wrap gap-3 justify-center">
                    {groupChips.map((c) => {
                      const idx = runningIndex++;
                      return <Chip key={idx} idx={idx} chip={c} />;
                    })}
                  </div>
                </motion.div>
              ));
            })()}
          </motion.div>
        ) : (
          <motion.div variants={stagger(0.06)} className="flex flex-wrap gap-3 justify-center max-w-[960px]">
            {chips.map((c, i) => (
              <motion.div key={i} variants={fadeUp}>
                <Chip idx={i} chip={c} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </motion.div>
    </Stage>
  );
}

function Chip({ idx, chip }: { idx: number; chip: { name: string; version?: string; } }) {
  return (
    <PointableItem
      orbTargetId={`chip_${idx}`}
      orbSize={28}
      orbPlacement="top-center"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-fg-faint/60 bg-void-800/60 text-body text-fg-soft font-display backdrop-blur-sm"
    >
      <span>{chip.name}</span>
      {chip.version && (
        <span className="font-mono text-caption text-fg-mute">{chip.version}</span>
      )}
    </PointableItem>
  );
}
